import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { Basket } from '../types/basket.type';
import { ADMIN_BASKETS_API_PATHS } from '../constants/baskets-api-paths.constants';

type UseAdminGetBasketByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  basket: Basket | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetBasketByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  basket: null,
};

export function useAdminGetBasketById({ basketId }: { basketId: string }): UseAdminGetBasketByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<Basket | undefined>(ADMIN_BASKETS_API_PATHS.GET_BASKET_BY_ID(basketId));

      setResult({ isLoading: false, isRefetching: false, basket: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération du panier'),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [basketId, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
